"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  api,
  loadTokens,
  type UserRewardsSummary,
} from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { SkeletonRewards } from "@/components/Skeleton";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import { formatDualMoney, type DualMoneyDisplay } from "@/lib/format";

/**
 * EPIC-027 STORY-696 — user liquidity-rewards dashboard.
 *
 * Lifetime + last-30d + per-market breakdown + recent distributions.
 * Per-order "earning rewards" visibility lives on /markets/[slug]
 * via STORY-697's orderbook reward-zone band.
 *
 * Refreshes on the existing `granbay.balance.changed` event so
 * recently-distributed rewards land instantly.
 */
export default function RewardsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [summary, setSummary] = useState<UserRewardsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  // EPIC-029 v4 — locked dual-display rule. Reward payouts denominated
  // in the matcher currency (BRL) — primary leg renders BRL truth +
  // secondary leg projects to the user's display_currency when it
  // differs and the FX rate is loaded. Returns null on non-finite
  // input so the caller can render an em-dash.
  const fmtCurrency = (cents: string): DualMoneyDisplay | null => {
    const n = Number(cents);
    if (!Number.isFinite(n)) return null;
    return formatDualMoney(n, "BRL", displayCurrency, usdcPerBrl);
  };

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(loginUrl());
      return;
    }
    void api
      .me()
      .then(() => {
        setAuthChecked(true);
        setAuthorized(true);
      })
      .catch(() => router.replace(loginUrl()));
  }, [router]);

  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    const refresh = () => {
      void api
        .getMyRewardsSummary()
        .then((res) => {
          if (!cancelled) setSummary(res);
        })
        .catch((err) => {
          if (!cancelled)
            setError(err instanceof Error ? err.message : t("rewards.loadFailed"));
        });
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener("granbay.balance.changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("granbay.balance.changed", onChange);
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  if (!authChecked) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-[800px] p-8 text-sm text-[var(--color-text3)]">
          Loading…
        </main>
      </>
    );
  }
  if (!authorized) return null;

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <div className="mb-4">
            <h1 className="text-[22px] font-bold tracking-tight">
              {t("rewards.title")}
            </h1>
            <p className="mt-1 text-[12px] text-[var(--color-text3)]">
              {t("rewards.subtitle")}
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="mb-4 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}

          {!summary && !error && <SkeletonRewards />}

          {summary && (
            <>
              {/* Totals */}
              <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Tile
                  label={t("rewards.lifetimeEarned")}
                  value={renderDualOrDash(fmtCurrency(summary.lifetimeCents))}
                  highlight
                />
                <Tile
                  label={t("rewards.last30Days")}
                  value={renderDualOrDash(fmtCurrency(summary.last30dCents))}
                />
                <Tile
                  label={t("rewards.marketsEarning")}
                  value={summary.perMarket.length.toString()}
                />
              </section>

              {summary.perMarket.length > 0 && (
                <section className="mb-6">
                  <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
                    {t("rewards.perMarket")}
                  </h2>
                  <ul className="space-y-2">
                    {summary.perMarket.map((m) => (
                      <li
                        key={m.marketId}
                        className="flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2"
                      >
                        <Link
                          href={`/markets/${m.marketSlug}`}
                          className="text-[13px] font-bold hover:text-[var(--color-brand)]"
                        >
                          {m.marketSlug}
                        </Link>
                        <span className="font-mono tabular-nums text-[13px] font-bold text-[var(--color-yes)]">
                          {renderDualOrDash(fmtCurrency(m.lifetimeCents))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {summary.recentDistributions.length > 0 && (
                <section className="mb-6">
                  <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
                    {t("rewards.recentDistributions")}
                  </h2>
                  <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
                          <th className="px-3 py-2 font-semibold">
                            {t("rewards.colDate")}
                          </th>
                          <th className="px-3 py-2 font-semibold">
                            {t("rewards.colMarket")}
                          </th>
                          <th className="px-3 py-2 text-right font-semibold">
                            {t("rewards.colPayout")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.recentDistributions.map((r, i) => (
                          <tr
                            key={`${r.distributionDate}-${r.marketSlug}-${i}`}
                            className="border-t border-[var(--color-border)]"
                          >
                            <td className="px-3 py-2 font-mono tabular-nums text-[var(--color-text2)]">
                              {r.distributionDate}
                            </td>
                            <td className="px-3 py-2">
                              <Link
                                href={`/markets/${r.marketSlug}`}
                                className="hover:underline"
                              >
                                {r.marketSlug}
                              </Link>
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-[var(--color-yes)]">
                              {renderDualOrDash(fmtCurrency(r.payoutCents))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {summary.lifetimeCents === "0" && (
                <section className="mb-6 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-[12px] text-[var(--color-text2)]">
                  <p className="font-bold text-[var(--color-text)]">
                    {t("rewards.noRewardsTitle")}
                  </p>
                  <p className="mt-1">{t("rewards.noRewardsBody")}</p>
                </section>
              )}

              <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-[12px] text-[var(--color-text2)]">
                <h3 className="mb-1 text-[12px] font-bold text-[var(--color-text)]">
                  {t("rewards.howRewardsWorkTitle")}
                </h3>
                <ul className="ml-4 list-disc space-y-1 text-[11px]">
                  <li>{t("rewards.howRewards1")}</li>
                  <li>{t("rewards.howRewards2")}</li>
                  <li>{t("rewards.howRewards3")}</li>
                  <li>{t("rewards.howRewards4")}</li>
                  <li>{t("rewards.howRewards5")}</li>
                </ul>
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
}

function Tile({
  label,
  value,
  highlight,
}: {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-md border p-3"
      style={{
        borderColor: highlight
          ? "var(--color-brand)"
          : "var(--color-border)",
        background: highlight
          ? "var(--color-brand-tint)"
          : "var(--color-bg)",
      }}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </div>
      <div className="mt-1 text-[20px] font-bold tabular-nums">{value}</div>
    </div>
  );
}

/**
 * Render a `DualMoneyDisplay` (or null em-dash for non-finite input)
 * with the locked dual-leg shape: primary in the parent's font weight,
 * secondary projection in a dimmer + smaller token. Same pattern as
 * the rest of the display-rule rollout (PR #1058 / #1059 / #1060 /
 * #1061 surfaces).
 */
function renderDualOrDash(dual: DualMoneyDisplay | null): ReactNode {
  if (dual === null) return "—";
  if (dual.secondary === null) return dual.primary;
  return (
    <>
      {dual.primary}
      <span className="ml-1 text-[12px] font-normal text-[var(--color-text3)]">
        (≈ {dual.secondary})
      </span>
    </>
  );
}

